import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { Card, CardContent, CardHeader } from './ui/card';


interface SegmentationConfig {
    espThresholds: {
        google: number;
        microsoft: number;
        yahoo: number;
        other: number;
    };
    tagAllocation: {
        openers: number;
        clickers: number;
        new: number;
        inactive: number;
    };
    newSubscriberRules: {
        maxNewPercentage: number;
        enableMinDays: boolean;
        minDays: number;
    };
    allocationSettings: {
        growthRate: number;
        quotaMethod: 'dynamic' | 'fixed';
        fixedQuota: number;
        minIspVolume: number;
    };
    reputationBuilding?: {
        enabled: boolean;
        minOpenRateThreshold: number;
        pauseOnboarding: boolean;
        pauseReengagement: boolean;
        pauseGrowth: boolean;
        targetISPs: string[];
        engagementLevel: string;
    };
    emergencyMode?: {
        enabled: boolean;
        targetISPs: string[];
        maxVolume: number;
        recencyDays: number;
        dailyGrowthPercent: number;
        autoDisableDays: number;
        startDate: string;
    };
}

interface ESPPerformance {
    allocated: number;
    opens: number;
    open_rate: number;
    estimated_final_rate: number;
}

interface TagPerformance {
    total_in_period: number;
    active: number;
    expected_open_rate: number | null;
}

interface EspDecisionInfo {
    status: string;
    estimated_rate: number;
    target_rate: number;
    segment_key: string;
    quota_multiplier: number;
    last_sent: number;
    quota: number;
    reason: string;
}

interface EspPerformanceStatus {
    total_quota: number;
    summary: string;
    send_info: {
        total_sent: number;
        hours_elapsed: number;
        campaign_id: string;
    };
    esp_decisions: { [key: string]: EspDecisionInfo };
}

interface SegmentationRulesProps {
    selectedEsp: string;
    geo: string;
}

export default function SegmentationRulesConfig({ selectedEsp, geo }: SegmentationRulesProps) {
    const [config, setConfig] = useState<SegmentationConfig>({
        espThresholds: {
            google: 25,
            microsoft: 22,
            yahoo: 20,
            other: 20
        },
        tagAllocation: {
            openers: 50,
            clickers: 25,
            new: 25,
            inactive: 0
        },
        newSubscriberRules: {
            maxNewPercentage: 10,
            enableMinDays: false,
            minDays: 0
        },
        allocationSettings: {
            growthRate: 10,
            quotaMethod: 'dynamic',
            fixedQuota: 1000,
            minIspVolume: 50
        },
        reputationBuilding: {
            enabled: false,
            minOpenRateThreshold: 35,
            pauseOnboarding: true,
            pauseReengagement: true,
            pauseGrowth: true,
            targetISPs: ['all'],
            engagementLevel: 'all_openers'
        },
        emergencyMode: {
            enabled: false,
            targetISPs: ['google'],
            maxVolume: 50,
            recencyDays: 3,
            dailyGrowthPercent: 20,
            autoDisableDays: 14,
            startDate: ''
        }
    });

    const [espPerformance, setEspPerformance] = useState<{ [key: string]: ESPPerformance } | null>(null);
    const [tagPerformance, setTagPerformance] = useState<{ [key: string]: TagPerformance } | null>(null);
    const [lastCampaign, setLastCampaign] = useState<any>(null);
    const [espPerfStatus, setEspPerfStatus] = useState<EspPerformanceStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetResult, setResetResult] = useState<{ success: boolean; message: string } | null>(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
    const [simLoading, setSimLoading] = useState(false);
    const [simHtml, setSimHtml] = useState<string | null>(null);

    // Default config — used to reset state when switching ESPs
    const defaultConfig: SegmentationConfig = {
        espThresholds: { google: 25, microsoft: 22, yahoo: 20, other: 20 },
        tagAllocation: { openers: 50, clickers: 25, new: 25, inactive: 0 },
        newSubscriberRules: { maxNewPercentage: 10, enableMinDays: false, minDays: 0 },
        allocationSettings: { growthRate: 10, quotaMethod: 'dynamic', fixedQuota: 1000, minIspVolume: 50 },
        reputationBuilding: { enabled: false, minOpenRateThreshold: 35, pauseOnboarding: true, pauseReengagement: true, pauseGrowth: true, targetISPs: ['all'], engagementLevel: 'all_openers' },
        emergencyMode: { enabled: false, targetISPs: ['google'], maxVolume: 50, recencyDays: 3, dailyGrowthPercent: 20, autoDisableDays: 14, startDate: '' }
    };

    // Load saved configuration on mount and when ESP/geo changes
    useEffect(() => {
        const loadConfig = async () => {
            if (!selectedEsp) return;
            try {
                const response = await fetch(`${API_BASE_URL}/api/segmentation/config?geo=${geo}&esp_id=${selectedEsp}`);
                if (response.ok) {
                    const savedConfig = await response.json();
                    if (savedConfig) {
                        // Reset to defaults first, then deep-merge saved config
                        // This prevents state bleeding between ESPs
                        setConfig({
                            ...defaultConfig,
                            ...savedConfig,
                            reputationBuilding: {
                                ...defaultConfig.reputationBuilding!,
                                ...(savedConfig.reputationBuilding || {})
                            },
                            emergencyMode: {
                                ...defaultConfig.emergencyMode!,
                                ...(savedConfig.emergencyMode || {})
                            }
                        });
                    } else {
                        setConfig(defaultConfig);
                    }
                } else {
                    setConfig(defaultConfig);
                }
            } catch (error) {
                console.error('Error loading configuration:', error);
                setConfig(defaultConfig);
            }
        };
        if (geo && selectedEsp) loadConfig();
    }, [geo, selectedEsp]);



    const fetchData = async () => {
        setLoading(true);
        try {
            // ... existing individual fetches ...
            // Just update campaign fetch to use dates

            // NOTE: URL construction needs to handle params manually since we calling fetch directly in previous implementation?
            // Wait, looking at lines 200+ (not shown), it likely uses `fetch(`/api/segmentation...`)`.
            // I need to intercept that.
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };


    // Fetch data when ESP/Geo changes
    useEffect(() => {
        if (!selectedEsp) {
            setLastCampaign(null);
            setEspPerformance(null);
            setTagPerformance(null);
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch ESP performance (last campaign breakdown)
                const espRes = await fetch(`${API_BASE_URL}/api/segmentation/last-campaign-esp-breakdown?geo=${geo}&esp_id=${selectedEsp}`);
                if (espRes.ok) {
                    const espData = await espRes.json();
                    setLastCampaign(espData);
                    setEspPerformance(espData.esp_performance || {});
                }

                // Fetch tag performance
                const tagRes = await fetch(`${API_BASE_URL}/api/segmentation/tag-historical-performance?geo=${geo}&days=30`);
                if (tagRes.ok) {
                    const tagData = await tagRes.json();
                    setTagPerformance(tagData.tag_performance || {});
                }

                // Fetch real ESP performance status (quotas & decisions)
                const perfRes = await fetch(`${API_BASE_URL}/api/segmentation/esp-performance-status?geo=${geo}&esp_id=${selectedEsp}`);
                if (perfRes.ok) {
                    const perfData = await perfRes.json();
                    setEspPerfStatus(perfData);
                }

            } catch (error) {
                console.error('Error fetching segmentation data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedEsp, geo]);

    // Show loading skeleton
    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                {/* Graph Skeleton */}

                {/* Stats Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                    <div className="h-32 bg-gray-200 rounded-lg"></div>
                </div>
                {/* Config Skeleton */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="h-96 bg-gray-200 rounded-lg"></div>
                    <div className="h-96 bg-gray-200 rounded-lg"></div>
                    <div className="h-96 bg-gray-200 rounded-lg"></div>
                </div>
            </div>
        );
    }


    // Use real data from ESP performance status API, with fallbacks
    const realLastSendSize = espPerfStatus?.send_info?.total_sent || 0;
    const realTotalQuota = espPerfStatus?.total_quota || 0;

    const stats = {
        lastSendSize: realLastSendSize,
        nextAllocation: realTotalQuota,
        hoursElapsed: espPerfStatus?.send_info?.hours_elapsed || 0,
        campaignId: espPerfStatus?.send_info?.campaign_id || '-'
    };

    if (!selectedEsp) {
        return (
            <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Select an ESP</h3>
                <p className="text-gray-500 text-center max-w-sm mt-1">
                    Please select an ESP System from the dropdown above to configure segmentation rules.
                </p>
            </div>
        );
    }

    // Get ESP status using the table's own estimated_final_rate vs configured target.
    // This ensures the Status badge is always consistent with the Est. 72h column.
    const getEspStatus = (esp: string, targetPct: number, estFinalRate?: number) => {
        // Use the breakdown API's estimated_final_rate (same data as the table's Est. 72h column)
        if (estFinalRate === undefined || estFinalRate === null) {
            return { label: '— No Data', className: 'bg-gray-100 text-gray-600', estimatedRate: null };
        }

        // Normalize: if the backend returns a percentage (e.g. 3.1) instead of a decimal (0.031),
        // convert it. Any value > 1.0 is clearly a percentage, not a decimal rate.
        const normalizedRate = estFinalRate > 1.0 ? estFinalRate / 100.0 : estFinalRate;

        const estPct = normalizedRate * 100;
        const targetDecimal = targetPct / 100.0;
        const criticalThreshold = targetDecimal * 0.8; // 20% below target = critical

        if (normalizedRate < 0.10) {
            return { label: `✗ Critical`, className: 'bg-red-100 text-red-800', estimatedRate: estPct };
        }
        if (normalizedRate >= targetDecimal) {
            return { label: `✓ Performing Well`, className: 'bg-green-100 text-green-800', estimatedRate: estPct };
        }
        if (normalizedRate >= criticalThreshold) {
            return { label: `⚠ Below Target`, className: 'bg-yellow-100 text-yellow-800', estimatedRate: estPct };
        }
        return { label: `✗ Critical`, className: 'bg-red-100 text-red-800', estimatedRate: estPct };
    };

    const getAllocationTotal = () => {
        return Object.values(config.tagAllocation).reduce((sum, val) => sum + val, 0);
    };

    const handleTagAllocationChange = (tag: keyof typeof config.tagAllocation, value: number) => {
        setConfig({
            ...config,
            tagAllocation: {
                ...config.tagAllocation,
                [tag]: Math.max(0, Math.min(100, value))
            }
        });
    };

    const handleBalance = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/segmentation/db-composition?geo=${geo}`);
            if (response.ok) {
                const data = await response.json();
                if (data.composition) {
                    setConfig(prev => ({
                        ...prev,
                        tagAllocation: {
                            openers: data.composition.openers || 0,
                            clickers: data.composition.clickers || 0,
                            new: data.composition.new || 0,
                            inactive: data.composition.inactive || 0
                        }
                    }));
                }
            } else {
                console.error('Failed to fetch composition');
            }
        } catch (error) {
            console.error('Error balancing:', error);
        }
    };



    const handleSave = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/segmentation/save-config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ geo, esp_id: selectedEsp, config })
            });

            if (response.ok) {
                alert('Configuration saved successfully!');
            } else {
                throw new Error('Failed to save configuration');
            }
        } catch (error) {
            console.error('Save error:', error);
            alert('Error saving configuration');
        }
    };

    const handleReset = () => {
        setConfig({
            espThresholds: { google: 25, microsoft: 22, yahoo: 20, other: 20 },
            tagAllocation: { openers: 50, clickers: 25, new: 25, inactive: 0 },
            newSubscriberRules: { maxNewPercentage: 10, enableMinDays: false, minDays: 0 },
            allocationSettings: { growthRate: 10, quotaMethod: 'dynamic', fixedQuota: 1000, minIspVolume: 50 },
            reputationBuilding: { enabled: false, minOpenRateThreshold: 35, pauseOnboarding: true, pauseReengagement: true, pauseGrowth: true, targetISPs: ['all'], engagementLevel: 'all_openers' },
            emergencyMode: { enabled: false, targetISPs: ['google'], maxVolume: 50, recencyDays: 3, dailyGrowthPercent: 20, autoDisableDays: 14, startDate: '' }
        });
    };

    const allocationTotal = getAllocationTotal();
    const allocationValid = allocationTotal === 100;



    return (
        <div className="space-y-6">

            {/* Analytics Grid */}


            {/* Statistics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-6">
                        <p className="text-sm text-gray-600 font-medium mb-1">Last SendSegment Size</p>
                        <p className="text-3xl font-bold text-blue-700">{stats.lastSendSize.toLocaleString()}</p>
                        <p className="text-xs text-blue-600 mt-1">
                            {stats.campaignId !== '-' ? `Campaign ${stats.campaignId}` : '—'}
                            {stats.hoursElapsed > 0 ? ` • ${Math.round(stats.hoursElapsed)}h ago` : ''}
                        </p>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <CardContent className="p-6">
                        <p className="text-sm text-gray-600 font-medium mb-1">Est. New Subscribers</p>
                        <p className="text-3xl font-bold text-purple-700">
                            {Math.round(
                                Object.entries(espPerformance || {}).reduce((sum, [esp, data]) => {
                                    const threshold = config.espThresholds[esp as keyof typeof config.espThresholds] || 20;
                                    const normRate = data.estimated_final_rate > 1.0 ? data.estimated_final_rate / 100.0 : data.estimated_final_rate;
                                    const isPerforming = (normRate * 100) >= threshold;
                                    return isPerforming ? sum + (data.allocated * (config.allocationSettings.growthRate / 100)) : sum;
                                }, 0)
                            ).toLocaleString()}
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                            Based on {config.allocationSettings.growthRate}% growth rate
                        </p>
                        <p className="text-[10px] text-purple-400 mt-1">
                            Total Quota: {stats.nextAllocation.toLocaleString()}
                        </p>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <CardContent className="p-6">
                        <p className="text-sm text-gray-600 font-medium mb-1">ESP Performance Summary</p>
                        <p className="text-lg font-bold text-green-700">
                            {(() => {
                                if (!espPerformance) return '0 /0 ESPs on target';
                                const entries = Object.entries(espPerformance).filter(([esp]) =>
                                    ['google', 'microsoft', 'yahoo', 'apple', 'other'].includes(esp)
                                );
                                const onTarget = entries.filter(([esp, data]) => {
                                    const threshold = config.espThresholds[esp as keyof typeof config.espThresholds] || 20;
                                    const normRate = data.estimated_final_rate > 1.0 ? data.estimated_final_rate / 100.0 : data.estimated_final_rate;
                                    return normRate >= threshold / 100.0;
                                }).length;
                                return `${onTarget} /${entries.length} ESPs on target`;
                            })()}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                            {espPerfStatus?.summary || 'Loading...'}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* ESP Performance Reporting */}
            {!loading && espPerformance && Object.keys(espPerformance).length > 0 && (
                <Card className="shadow-md border-gray-300">
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                        <h2 className="text-xl font-bold">📊 Last Campaign Performance - By ESP</h2>
                        {lastCampaign && (
                            <p className="text-sm text-gray-600 mt-1">
                                <strong>{lastCampaign.campaign_name}</strong> • Sent {lastCampaign.hours_since_send}h ago
                            </p>
                        )}
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="overflow-x-auto w-full">
                            <table className="w-full min-w-[600px] text-sm">
                                <thead className="bg-gray-100 border-b-2 border-gray-300">
                                    <tr>
                                        <th className="py-3 px-4 text-left font-semibold">ESP</th>
                                        <th className="py-3 px-4 text-right font-semibold">Allocated</th>
                                        <th className="py-3 px-4 text-right font-semibold">Opens</th>
                                        <th className="py-3 px-4 text-right font-semibold">Open Rate</th>
                                        <th className="py-3 px-4 text-right font-semibold">Est. 72h</th>
                                        <th className="py-3 px-4 text-center font-semibold">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {Object.entries(espPerformance)
                                        .filter(([esp, data]) => {
                                            const alwaysShow = ['google', 'microsoft', 'yahoo', 'apple', 'other'];
                                            if (alwaysShow.includes(esp)) return true;
                                            const total = lastCampaign?.total_recipients || 1;
                                            return (data.allocated / total) >= 0.03;
                                        })
                                        .sort(([, a], [, b]) => b.allocated - a.allocated)
                                        .map(([esp, data]) => {
                                            const threshold = config.espThresholds[esp as keyof typeof config.espThresholds] || 20;
                                            const status = getEspStatus(esp, threshold, data.estimated_final_rate);
                                            return (
                                                <tr key={esp} className="hover:bg-gray-50">
                                                    <td className="py-3 px-4 font-semibold capitalize">{esp}</td>
                                                    <td className="py-3 px-4 text-right">
                                                        {data.allocated.toLocaleString()}
                                                        <span className="text-xs text-gray-500 ml-1">
                                                            ({((data.allocated / (lastCampaign?.total_recipients || 1)) * 100).toFixed(0)}%)
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-right font-medium">{data.opens}</td>
                                                    <td className="py-3 px-4 text-right font-bold text-blue-700">
                                                        {((data.open_rate > 1.0 ? data.open_rate : data.open_rate * 100)).toFixed(1)}%
                                                    </td>
                                                    <td className="py-3 px-4 text-right text-gray-600">
                                                        ~{((data.estimated_final_rate > 1.0 ? data.estimated_final_rate : data.estimated_final_rate * 100)).toFixed(1)}%
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${status.className}`}>
                                                            {status.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-gray-500 mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                            💡 <strong>Tip:</strong> ESPs below your configured threshold will be EXCLUDED from the next allocation.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Reputation Building Mode */}
            <Card className={`shadow-md border-2 transition-all ${
                config.reputationBuilding?.enabled
                    ? 'border-amber-400 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50'
                    : 'border-gray-200 bg-white'
            }`}>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                                config.reputationBuilding?.enabled
                                    ? 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-200'
                                    : 'bg-gray-100'
                            }`}>
                                🛡️
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Reputation Building Mode</h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {config.reputationBuilding?.enabled
                                        ? '🔥 Active — Only sending to engaged subscribers (Active + Warm tiers)'
                                        : 'Disabled — Normal allocation (all tiers)'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setConfig(prev => ({
                                ...prev,
                                reputationBuilding: {
                                    ...prev.reputationBuilding!,
                                    enabled: !prev.reputationBuilding?.enabled
                                }
                            }))}
                            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                config.reputationBuilding?.enabled
                                    ? 'bg-amber-500 focus:ring-amber-500'
                                    : 'bg-gray-300 focus:ring-gray-400'
                            }`}
                        >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                config.reputationBuilding?.enabled ? 'translate-x-8' : 'translate-x-1'
                            }`} />
                        </button>
                    </div>

                    {config.reputationBuilding?.enabled && (
                        <div className="mt-5 pt-5 border-t border-amber-200 space-y-4">
                            {/* ISP Targeting */}
                            <div className="bg-white/60 rounded-lg p-4 border border-amber-200">
                                <label className="text-sm font-semibold text-gray-700 block mb-2">Target ISPs</label>
                                <div className="flex flex-wrap gap-2">
                                    {['all', 'google', 'microsoft', 'yahoo', 'other'].map(isp => {
                                        const isSelected = config.reputationBuilding?.targetISPs?.includes(isp);
                                        return (
                                            <button
                                                key={isp}
                                                onClick={() => {
                                                    setConfig(prev => {
                                                        const current = prev.reputationBuilding?.targetISPs || ['all'];
                                                        let next: string[];
                                                        if (isp === 'all') {
                                                            next = ['all'];
                                                        } else {
                                                            const without = current.filter(i => i !== 'all' && i !== isp);
                                                            next = isSelected ? without : [...without, isp];
                                                            if (next.length === 0) next = ['all'];
                                                        }
                                                        return {
                                                            ...prev,
                                                            reputationBuilding: { ...prev.reputationBuilding!, targetISPs: next }
                                                        };
                                                    });
                                                }}
                                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                                                    isSelected
                                                        ? 'bg-amber-500 text-white border-amber-600 shadow-sm'
                                                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                                                }`}
                                            >
                                                {isp === 'all' ? '🌐 All ISPs' : isp === 'google' ? '📧 Google' : isp === 'microsoft' ? '📬 Microsoft' : isp === 'yahoo' ? '📮 Yahoo' : '📋 Other'}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5">Only apply reputation filtering to subscribers from these ISPs</p>
                            </div>

                            {/* Engagement Level */}
                            <div className="bg-white/60 rounded-lg p-4 border border-amber-200">
                                <label className="text-sm font-semibold text-gray-700 block mb-2">Engagement Level Filter</label>
                                <select
                                    value={config.reputationBuilding?.engagementLevel || 'all_openers'}
                                    onChange={(e) => setConfig(prev => ({
                                        ...prev,
                                        reputationBuilding: { ...prev.reputationBuilding!, engagementLevel: e.target.value }
                                    }))}
                                    className="w-full p-2.5 rounded-lg border border-amber-300 bg-white text-sm font-medium focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                                >
                                    <option value="super_engaged">🔥 Super-engaged (opened in last 3 days)</option>
                                    <option value="active">✅ Active (opened in last 14 days)</option>
                                    <option value="warm">🌤️ Warm (opened in last 30 days)</option>
                                    <option value="all_openers">📨 All openers (any opener)</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1.5">Only include openers within this engagement window for targeted ISPs</p>
                            </div>

                            {/* Granular pause toggles */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { key: 'pauseOnboarding' as const, label: 'Pause Onboarding', icon: '🚫', desc: 'Skip new non-openers' },
                                    { key: 'pauseReengagement' as const, label: 'Pause Re-engagement', icon: '⏸️', desc: 'Skip cold subscriber drips' },
                                    { key: 'pauseGrowth' as const, label: 'Pause Growth', icon: '🌱', desc: 'No fresh recruits added' }
                                ].map(item => (
                                    <div
                                        key={item.key}
                                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                            config.reputationBuilding?.[item.key]
                                                ? 'bg-red-50 border-red-200'
                                                : 'bg-green-50 border-green-200'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">{item.icon}</span>
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                                                <p className="text-xs text-gray-500">{item.desc}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setConfig(prev => ({
                                                ...prev,
                                                reputationBuilding: {
                                                    ...prev.reputationBuilding!,
                                                    [item.key]: !prev.reputationBuilding?.[item.key]
                                                }
                                            }))}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                config.reputationBuilding?.[item.key]
                                                    ? 'bg-red-400'
                                                    : 'bg-green-400'
                                            }`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                                config.reputationBuilding?.[item.key] ? 'translate-x-6' : 'translate-x-1'
                                            }`} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Min open rate threshold */}
                            <div className="bg-white/60 rounded-lg p-4 border border-amber-200">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-semibold text-gray-700">Min Open Rate Threshold</label>
                                    <span className="text-lg font-bold text-amber-600">{config.reputationBuilding?.minOpenRateThreshold || 35}%</span>
                                </div>
                                <input
                                    type="range" min="20" max="50"
                                    value={config.reputationBuilding?.minOpenRateThreshold || 35}
                                    onChange={(e) => setConfig(prev => ({
                                        ...prev,
                                        reputationBuilding: { ...prev.reputationBuilding!, minOpenRateThreshold: parseInt(e.target.value) }
                                    }))}
                                    className="w-full h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>

                            {/* Seed Emails */}
                            <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
                                <p className="text-xs font-semibold text-gray-700 mb-1">🌱 Seed Emails (always included)</p>
                                <p className="text-xs text-gray-500 font-mono leading-relaxed">
                                    lars.hedlund84@gmail.com, kristoffer.afima@gmail.com, fredywohnmobil2@gmail.com, chris.mads95@gmail.com, willerenger@gmail.com, kris.mads@outlook.com, antonthomsen94@gmail.com, kryberg@live.dk, antonthomsen95@yahoo.com
                                </p>
                            </div>

                            <div className="bg-amber-100/50 rounded-lg p-3 border border-amber-200">
                                <p className="text-xs text-amber-800">
                                    <strong>💡 How it works:</strong> Only subscribers from targeted ISPs matching your engagement level are included.
                                    Non-targeted ISP subscribers pass through normally. Seed emails are always included.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── Emergency Recovery Mode ── */}
                    <div className={`mt-4 rounded-xl border-2 transition-all ${
                        config.emergencyMode?.enabled
                            ? 'border-red-400 bg-gradient-to-r from-red-50 via-rose-50 to-red-50 p-4'
                            : 'border-dashed border-gray-300 p-3'
                    }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-xl">{config.emergencyMode?.enabled ? '🚨' : '🔇'}</span>
                                <div>
                                    <p className="text-sm font-bold text-gray-900">Emergency Recovery Mode</p>
                                    <p className="text-xs text-gray-500">
                                        {config.emergencyMode?.enabled
                                            ? `Active — Volume cap with progressive warm-up`
                                            : 'Severe deliverability crisis? Activate to throttle volume'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setConfig(prev => ({
                                    ...prev,
                                    emergencyMode: {
                                        ...prev.emergencyMode!,
                                        enabled: !prev.emergencyMode?.enabled,
                                        startDate: !prev.emergencyMode?.enabled ? new Date().toISOString().split('T')[0] : prev.emergencyMode?.startDate || ''
                                    }
                                }))}
                                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                                    config.emergencyMode?.enabled ? 'bg-red-500' : 'bg-gray-300'
                                }`}
                            >
                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                    config.emergencyMode?.enabled ? 'translate-x-8' : 'translate-x-1'
                                }`} />
                            </button>
                        </div>

                        {config.emergencyMode?.enabled && (
                            <div className="mt-4 pt-4 border-t border-red-200 space-y-3">
                                {/* Emergency ISP targeting */}
                                <div>
                                    <label className="text-xs font-semibold text-gray-700 block mb-1">Target ISPs</label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {['google', 'microsoft', 'yahoo', 'other'].map(isp => {
                                            const isSelected = config.emergencyMode?.targetISPs?.includes(isp);
                                            return (
                                                <button
                                                    key={isp}
                                                    onClick={() => setConfig(prev => {
                                                        const current = prev.emergencyMode?.targetISPs || ['google'];
                                                        const next = isSelected
                                                            ? current.filter(i => i !== isp)
                                                            : [...current, isp];
                                                        return {
                                                            ...prev,
                                                            emergencyMode: { ...prev.emergencyMode!, targetISPs: next.length > 0 ? next : ['google'] }
                                                        };
                                                    })}
                                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all border ${
                                                        isSelected
                                                            ? 'bg-red-500 text-white border-red-600'
                                                            : 'bg-gray-100 text-gray-600 border-gray-200'
                                                    }`}
                                                >
                                                    {isp === 'google' ? '📧 Google' : isp === 'microsoft' ? '📬 Microsoft' : isp === 'yahoo' ? '📮 Yahoo' : '📋 Other'}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1">Start Volume</label>
                                        <input type="number" min="1" max="10000"
                                            value={config.emergencyMode?.maxVolume || 50}
                                            onChange={(e) => setConfig(prev => ({
                                                ...prev,
                                                emergencyMode: { ...prev.emergencyMode!, maxVolume: parseInt(e.target.value) || 50 }
                                            }))}
                                            className="w-full p-2 rounded border border-red-300 text-sm font-mono focus:ring-2 focus:ring-red-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1">Recency (days)</label>
                                        <input type="number" min="1" max="30"
                                            value={config.emergencyMode?.recencyDays || 3}
                                            onChange={(e) => setConfig(prev => ({
                                                ...prev,
                                                emergencyMode: { ...prev.emergencyMode!, recencyDays: parseInt(e.target.value) || 3 }
                                            }))}
                                            className="w-full p-2 rounded border border-red-300 text-sm font-mono focus:ring-2 focus:ring-red-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1">Daily Growth %</label>
                                        <input type="number" min="0" max="100"
                                            value={config.emergencyMode?.dailyGrowthPercent || 20}
                                            onChange={(e) => setConfig(prev => ({
                                                ...prev,
                                                emergencyMode: { ...prev.emergencyMode!, dailyGrowthPercent: parseInt(e.target.value) || 20 }
                                            }))}
                                            className="w-full p-2 rounded border border-red-300 text-sm font-mono focus:ring-2 focus:ring-red-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 block mb-1">Auto-off (days)</label>
                                        <input type="number" min="1" max="30"
                                            value={config.emergencyMode?.autoDisableDays || 14}
                                            onChange={(e) => setConfig(prev => ({
                                                ...prev,
                                                emergencyMode: { ...prev.emergencyMode!, autoDisableDays: parseInt(e.target.value) || 14 }
                                            }))}
                                            className="w-full p-2 rounded border border-red-300 text-sm font-mono focus:ring-2 focus:ring-red-400"
                                        />
                                    </div>
                                </div>

                                {/* Daily cap preview */}
                                {(() => {
                                    const startDate = config.emergencyMode?.startDate || '';
                                    const maxVol = config.emergencyMode?.maxVolume || 50;
                                    const growth = config.emergencyMode?.dailyGrowthPercent || 20;
                                    const today = new Date().toISOString().split('T')[0];
                                    let daysElapsed = 0;
                                    if (startDate) {
                                        daysElapsed = Math.max(0, Math.floor((new Date(today).getTime() - new Date(startDate).getTime()) / 86400000));
                                    }
                                    const todayCap = Math.floor(maxVol * Math.pow(1 + growth / 100, daysElapsed));
                                    return (
                                        <div className="bg-red-100/50 rounded-lg p-3 border border-red-200">
                                            <p className="text-xs text-red-800">
                                                <strong>📊 Today (day {daysElapsed}):</strong> Max <strong>{todayCap.toLocaleString()}</strong> sends to targeted ISPs
                                                {' '}| Started: {startDate || 'today'}
                                                {' '}| Target: {config.emergencyMode?.targetISPs?.join(', ')}
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Configuration Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ESP Open Rate Thresholds */}
                <Card className="shadow-sm border-gray-200">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl">
                                📧
                            </div>
                            <h2 className="text-xl font-bold">ESP Open Rate Thresholds</h2>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(config.espThresholds).map(([esp, rate]) => {
                            const status = getEspStatus(esp, rate);
                            const decision = espPerfStatus?.esp_decisions?.[esp];
                            return (
                                <div key={esp} className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold capitalize">{esp}</span>
                                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${status.className}`}>
                                            {status.label}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="relative flex-1 h-6 flex items-center">
                                            {/* Progress bar showing real estimated rate */}
                                            <div className="absolute inset-x-0 h-2 bg-gray-200 rounded-lg overflow-hidden">
                                                {status.estimatedRate !== null && (
                                                    <div
                                                        className={`h-full rounded-lg transition-all ${decision?.status === 'above_target' ? 'bg-green-500' :
                                                            decision?.status === 'below_target' ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                        style={{ width: `${Math.min(status.estimatedRate / 35 * 100, 100)}%` }}
                                                    />
                                                )}
                                            </div>
                                            {/* Slider overlaid on progress bar */}
                                            <input
                                                type="range"
                                                min="0"
                                                max="35"
                                                value={rate}
                                                onChange={(e) => setConfig({
                                                    ...config,
                                                    espThresholds: { ...config.espThresholds, [esp]: parseInt(e.target.value) }
                                                })}
                                                className="relative w-full h-2 bg-transparent rounded-lg appearance-none cursor-pointer z-10"
                                            />
                                        </div>
                                        <span className="text-lg font-bold text-blue-600 min-w-[50px] text-right">
                                            {rate}%
                                        </span>
                                    </div>
                                    {/* Real data details */}
                                    {decision && (
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>Est. rate: {(decision.estimated_rate * 100).toFixed(1)}%</span>
                                            <span>{decision.last_sent.toLocaleString()} sent → {decision.quota} new quota</span>
                                            <span className="font-medium">{decision.segment_key}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <div className="text-xs text-gray-500 mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                            🎯 Target open rate threshold. Performance rules:
                            <div className="mt-1 space-y-0.5">
                                <div>✅ <strong>Above target:</strong> Full quota (1×), New_30 segment</div>
                                <div>⚠️ <strong>Below target:</strong> Half quota (0.5×), New_14 segment</div>
                                <div>❌ <strong>Critical (&gt;20% below):</strong> Stop adding (0×), New_7 only</div>
                            </div>
                            <div className="mt-2 text-xs font-semibold text-orange-700">
                                ⚠️ Max threshold: 35% (realistic for Gmail/Outlook)
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Tag Allocation & Performance */}
                <Card className="shadow-sm border-gray-200">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl">
                                🏷️
                            </div>
                            <h2 className="text-xl font-bold">Tag Allocation & Performance</h2>
                        </div>
                        <button
                            onClick={handleBalance}
                            className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded hover:bg-blue-200 transition-colors"
                            title="Set allocation based on current database composition"
                        >
                            Need Balance?
                        </button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(config.tagAllocation).map(([tag, percentage]) => {
                            const perf = tagPerformance?.[tag];
                            return (
                                <div key={tag} className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-semibold capitalize">{tag}</span>
                                        {perf && perf.expected_open_rate !== null && (
                                            <span className="text-xs text-green-700 font-semibold">
                                                Est. {(perf.expected_open_rate * 100).toFixed(1)}% open rate
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            value={percentage}
                                            onChange={(e) => handleTagAllocationChange(tag as any, parseInt(e.target.value))}
                                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        />
                                        <span className="text-lg font-bold text-purple-600 min-w-[50px] text-right">
                                            {percentage}%
                                        </span>
                                    </div>
                                    {perf && (
                                        <p className="text-xs text-gray-500">
                                            Based on: {perf.active}/{perf.total_in_period} active in last 30d
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                        <div className={`text-sm font-bold p-3 rounded-lg ${allocationValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            Total: {allocationTotal}% {allocationValid ? '✓' : '✗ Must equal 100%'}
                        </div>
                        <p className="text-xs text-gray-500 mt-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                            📌 Set % distribution by tag quality. Example: 50% Openers, 25% Clickers, 25% New = Out of 100 new subscribers, 50 will be Openers, 25 Clickers, 25 New.
                        </p>
                    </CardContent>
                </Card>

                {/* Allocation Settings */}
                <Card className="shadow-sm border-gray-200">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl">
                                📊
                            </div>
                            <h2 className="text-xl font-bold">Growth & Allocation</h2>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <label className="text-sm font-semibold text-gray-700 block mb-2">
                                Maximum New Subscriber %
                            </label>
                            <input
                                type="number"
                                value={config.newSubscriberRules.maxNewPercentage}
                                onChange={(e) => setConfig({
                                    ...config,
                                    newSubscriberRules: {
                                        ...config.newSubscriberRules,
                                        maxNewPercentage: parseInt(e.target.value) || 0
                                    }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                min="0"
                                max="100"
                            />
                            <p className="text-xs text-gray-500 mt-1">Max % of new subscribers per campaign</p>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-semibold text-gray-700">Campaign Growth Rate</label>
                                <span className="text-lg font-bold text-blue-600">{config.allocationSettings.growthRate}%</span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={config.allocationSettings.growthRate}
                                onChange={(e) => setConfig({
                                    ...config,
                                    allocationSettings: {
                                        ...config.allocationSettings,
                                        growthRate: parseInt(e.target.value)
                                    }
                                })}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <p className="text-xs text-gray-500 mt-1">Growth % for each campaign send</p>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-semibold text-gray-700">🔻 Min ISP Volume (Floor)</label>
                                <span className="text-lg font-bold text-orange-600">{config.allocationSettings.minIspVolume ?? 50}</span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="500"
                                step="10"
                                value={config.allocationSettings.minIspVolume ?? 50}
                                onChange={(e) => setConfig({
                                    ...config,
                                    allocationSettings: {
                                        ...config.allocationSettings,
                                        minIspVolume: parseInt(e.target.value)
                                    }
                                })}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            />
                            <p className="text-xs text-gray-500 mt-1">Each major ISP (Google, Microsoft, Yahoo, Apple) always gets at least this many subscribers, even with 0 quota. Prevents death spiral.</p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg border-l-4 border-blue-500">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Calculation Preview (Real Data)</p>
                            <div className="space-y-1 text-xs text-gray-600">
                                <p>Last SendSegment: <strong className="text-gray-900">{stats.lastSendSize.toLocaleString()} subscribers</strong></p>
                                <p>Growth Rate: <strong className="text-gray-900">{config.allocationSettings.growthRate}%</strong></p>
                                <p className="text-sm text-blue-700 font-bold mt-2">
                                    Next Allocation (perf-adjusted): <strong>{stats.nextAllocation.toLocaleString()} subscribers</strong>
                                </p>
                                <p className="text-sm text-gray-900 font-bold">
                                    Next SendSegment: <strong>{(stats.lastSendSize + stats.nextAllocation).toLocaleString()} subscribers</strong>
                                </p>
                                {espPerfStatus && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                        <p className="font-semibold text-gray-700 mb-1">Per-ESP Quota Breakdown:</p>
                                        {Object.entries(espPerfStatus.esp_decisions).map(([esp, d]) => (
                                            <p key={esp} className="capitalize">
                                                {d.status === 'above_target' ? '✅' : d.status === 'below_target' ? '⚠️' : '❌'}{' '}
                                                {esp}: {d.last_sent.toLocaleString()} sent → <strong>{d.quota}</strong> new ({d.segment_key})
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-end gap-3">
                <button
                    onClick={async () => {
                        if (!selectedEsp) return;
                        setSimLoading(true);
                        try {
                            const resp = await fetch(`${API_BASE_URL}/api/allocation/simulate?geo=${geo}&esp_id=${selectedEsp}`);
                            const text = await resp.text();
                            // API Gateway wraps response in {statusCode, body} — extract the HTML
                            let html = text;
                            try {
                                const json = JSON.parse(text);
                                if (json.body) html = json.body;
                            } catch { /* already raw HTML */ }
                            setSimHtml(html);
                        } catch (err: any) {
                            alert('Simulation failed: ' + err.message);
                        } finally {
                            setSimLoading(false);
                        }
                    }}
                    disabled={simLoading || !selectedEsp}
                    className={`px-5 py-2.5 font-semibold rounded-lg transition-all border-2 ${
                        simLoading || !selectedEsp
                            ? 'bg-purple-100 border-purple-300 text-purple-400 cursor-not-allowed'
                            : 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100 hover:border-purple-400'
                    }`}
                >
                    {simLoading ? (
                        <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Simulating...
                        </span>
                    ) : '🔬 Simulate Allocation'}
                </button>
                <button
                    onClick={async () => {
                        if (!selectedEsp) return;
                        setSyncLoading(true);
                        setSyncResult(null);
                        try {
                            const resp = await fetch(`${API_BASE_URL}/api/ops/trigger`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    action: 'sync_active_subscribers',
                                    geo,
                                    esp_id: selectedEsp
                                })
                            });
                            const data = await resp.json();
                            if (data.error) {
                                setSyncResult({ success: false, message: data.error });
                            } else {
                                // Based on lambda_function.py, the success result might be wrapped in the 'results' array if triggered as an op
                                const syncStats = data.results && data.results[0]?.result?.stats
                                    ? data.results[0].result.stats
                                    : data.stats;

                                if (syncStats) {
                                    setSyncResult({
                                        success: true,
                                        message: `Synced ${syncStats.esp_count?.toLocaleString()} from ESP. Deactivated: ${syncStats.deactivated?.toLocaleString()}, Reactivated: ${syncStats.reactivated?.toLocaleString()}`
                                    });
                                } else if (data.results && data.results[0]?.result?.error) {
                                    setSyncResult({ success: false, message: data.results[0].result.error });
                                } else {
                                    setSyncResult({
                                        success: true,
                                        message: data.summary || "Sync started successfully"
                                    });
                                }
                            }
                        } catch (err: any) {
                            setSyncResult({ success: false, message: err.message });
                        } finally {
                            setSyncLoading(false);
                        }
                    }}
                    disabled={syncLoading || !selectedEsp}
                    className={`px-5 py-2.5 font-semibold rounded-lg transition-all border-2 ${syncLoading || !selectedEsp
                        ? 'bg-blue-100 border-blue-300 text-blue-400 cursor-not-allowed'
                        : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 hover:border-blue-400'
                        }`}
                >
                    {syncLoading ? (
                        <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Syncing...
                        </span>
                    ) : '🔄 Sync ESP Subscribers'}
                </button>
                <button
                    onClick={async () => {
                        if (!window.confirm('This will reset send_status for ALL contacts in the campaign.\n\nSelected contacts (from S3 segments) → "ready"\nEveryone else → "inactive"\n\nProceed?')) return;
                        setResetLoading(true);
                        setResetResult(null);
                        try {
                            const resp = await fetch(`${API_BASE_URL}/api/segmentation/reset-send-status`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' }
                            });
                            const data = await resp.json();
                            if (data.error) {
                                setResetResult({ success: false, message: data.error });
                            } else {
                                setResetResult({
                                    success: true,
                                    message: `Done! ${data.send_segment_size?.toLocaleString()} ready / ${data.inactive_count?.toLocaleString()} inactive (${data.imported?.toLocaleString()} imported)`
                                });
                            }
                        } catch (err: any) {
                            setResetResult({ success: false, message: err.message });
                        } finally {
                            setResetLoading(false);
                        }
                    }}
                    disabled={resetLoading}
                    className={`px-5 py-2.5 font-semibold rounded-lg transition-all border-2 ${resetLoading
                        ? 'bg-orange-100 border-orange-300 text-orange-400 cursor-wait'
                        : 'bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100 hover:border-orange-400'
                        }`}
                >
                    {resetLoading ? (
                        <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Resetting...
                        </span>
                    ) : '🔄 Reset Send Status'}
                </button>
                <button
                    onClick={handleReset}
                    className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                    Reset to Defaults
                </button>
                <button
                    onClick={handleSave}
                    disabled={!allocationValid}
                    className={`px-6 py-2.5 font-semibold rounded-lg shadow-md transition-all transform ${allocationValid
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white hover:-translate-y-0.5'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                >
                    💾 Save Configuration
                </button>
            </div>

            {resetResult && (
                <div className={`rounded-lg p-4 ${resetResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-sm font-semibold ${resetResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {resetResult.success ? '✅' : '❌'} {resetResult.message}
                    </p>
                </div>
            )}

            {syncResult && (
                <div className={`rounded-lg p-4 ${syncResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`text-sm font-semibold ${syncResult.success ? 'text-green-800' : 'text-red-800'}`}>
                        {syncResult.success ? '✅' : '❌'} {syncResult.message}
                    </p>
                </div>
            )}

            {!allocationValid && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-red-800">
                        ⚠️ Tag allocation percentages must sum to exactly 100% before saving.
                    </p>
                </div>
            )}

            {/* Simulation Report Modal */}
            {simHtml && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
                     onClick={() => setSimHtml(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
                         onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-lg font-bold text-gray-800">🔬 Allocation Simulation Report</h2>
                            <button onClick={() => setSimHtml(null)}
                                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
                        </div>
                        <iframe
                            srcDoc={simHtml}
                            className="flex-1 w-full border-0"
                            title="Simulation Report"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
